import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import type { ReactNode } from 'react';
import type { SortOrder } from './sort';

export type { SortOrder, SortState } from './sort';
export { nextSort } from './sort';

interface SortableHeaderProps {
  /** API field name passed to the backend `sort_by` (must be in the handler's allowlist). */
  field: string;
  label: ReactNode;
  /** Currently active sort field (null = default order). */
  activeField: string | null;
  activeOrder: SortOrder;
  /** Called with the column's field on click; parent applies nextSort(). */
  onSort: (field: string) => void;
  align?: 'left' | 'right' | 'center';
  className?: string;
  /** Optional trailing node (e.g. a HelpTooltip) rendered after the label. */
  trailing?: ReactNode;
}

/**
 * A `<th>` that sorts server-side. Server-side is mandatory: client-side array
 * sorting would only reorder the current page of a paginated table — correct-
 * looking, silently wrong. The parent wires the resulting sort into useList's
 * `sorters`, which the data provider maps to `sort_by`/`sort_order`.
 */
export function SortableHeader({
  field,
  label,
  activeField,
  activeOrder,
  onSort,
  align = 'left',
  className = '',
  trailing,
}: SortableHeaderProps) {
  const isActive = activeField === field;
  const Icon = !isActive ? ChevronsUpDown : activeOrder === 'asc' ? ChevronUp : ChevronDown;
  const alignCls =
    align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';
  const thAlign = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';

  const nextLabel = !isActive
    ? 'descending'
    : activeOrder === 'desc'
      ? 'ascending'
      : 'unsorted';

  return (
    <th className={`px-4 py-3 ${thAlign} text-sm font-medium text-muted-foreground ${className}`}>
      <button
        type="button"
        onClick={() => onSort(field)}
        aria-label={`Sort by ${typeof label === 'string' ? label : field}: ${nextLabel}`}
        className={`group inline-flex items-center gap-1 ${alignCls} ${
          isActive ? 'text-foreground' : 'hover:text-foreground'
        }`}
      >
        <span>{label}</span>
        <Icon
          className={`h-3.5 w-3.5 shrink-0 ${
            isActive
              ? 'text-primary'
              : 'text-muted-foreground/40 group-hover:text-muted-foreground'
          }`}
        />
        {trailing}
      </button>
    </th>
  );
}
